import { ImageMetadata } from '@lukso/lsp4-contracts'

import { ProfilePictures } from '@/shared/models/Profile'
import { urlResolver } from '@/shared/config_urls'

const PROFILE_IMAGE_MIN_WIDTH = 300
const PROFILE_IMAGE_MAX_HEIGHT = 600
const COVER_IMAGE_MIN_WIDTH = 800
const COVER_IMAGE_MAX_HEIGHT = 800

const convertBlobToBase64 = (blob: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('error', event => {
      event.preventDefault()
      reject(new Error('failed'))
    })
    reader.addEventListener('load', () => {
      resolve(reader.result)
    })
    reader.readAsDataURL(blob)
  })

export const fetchBlobAndConvertToBase64 = async (
  request: Request
): Promise<unknown> => {
  return fetch(request)
    .then(response => response.blob())
    .then(convertBlobToBase64)
}

export const fetchAndConvertImage = async (
  imageUrl: string
): Promise<string> => {
  const request = new Request(urlResolver.resolveUrl(imageUrl))
  return (await fetchBlobAndConvertToBase64(request)) as string
}

/**
 * Gets a correct image url from an array of possible image
 * by checking the max height and min width of the image.
 *
 * @param LSP3ProfileImage[] images - an array of images to check
 * @param number minWidth - min width
 * @param number maxHeight - max height
 * @returns url of the image
 */
export const getImageUrlBySize = (
  images: ImageMetadata[],
  minWidth: number,
  maxHeight: number
): string | undefined => {
  for (const image of images) {
    if (image.width >= minWidth && image.height <= maxHeight) {
      return image.url
    }
  }
  return images.length > 0 ? images[0].url : undefined
}

/**
 * Fetches user's profile/cover images and selects the correct image
 * to be stored in the user's model/database.
 *
 * @param string profileImage - new profile image
 * @param string backgroundImage - new cover image
 * @param string existingCoverPicture - current user's cover picture
 * @param string existingProfilePicture - current user' profile picture
 * @returns user's cover and profile images
 */
export const getProfileImages = async ({
  profileImage,
  backgroundImage,
}: {
  profileImage?: ImageMetadata[]
  backgroundImage?: ImageMetadata[]
}): Promise<ProfilePictures> => {
  let coverPicture: string | undefined, profilePicture: string | undefined

  if (backgroundImage && backgroundImage.length > 0) {
    const foundImage = getImageUrlBySize(
      backgroundImage,
      COVER_IMAGE_MIN_WIDTH,
      COVER_IMAGE_MAX_HEIGHT
    )
    if (foundImage) {
      coverPicture = await fetchAndConvertImage(foundImage)
    }
  }

  if (profileImage && profileImage.length > 0) {
    const foundImage = getImageUrlBySize(
      profileImage,
      PROFILE_IMAGE_MIN_WIDTH,
      PROFILE_IMAGE_MAX_HEIGHT
    )
    if (foundImage) {
      profilePicture = await fetchAndConvertImage(foundImage)
    }
  }

  return {
    coverPicture,
    profilePicture,
  }
}
